import { createFileRoute } from "@tanstack/react-router";
import { ContactsBrowser } from "@/components/contacts/ContactsBrowser";
import { useSetPageContext } from "@/hooks/use-page-context";
import { useSession } from "@/hooks/use-session";

export const Route = createFileRoute("/_app/_/contacts/")({
	component: ContactsListPage,
});

/**
 * Contacts page (2026-05-25 Andrew correction):
 *
 * Shows every contact the campaign actually has on file — donors,
 * volunteers, CSV imports, AND voter-file imports — sourced from the
 * ``contacts`` table. Previously this page rendered the campaign's
 * voter-file scope (e.g. all of RI = 761k rows) which misled users
 * about how many real contacts they had. The previous multi-tab
 * layout (Saved Contacts / Engagement Pipeline / Follow-Ups / Action
 * Groups / Text Banking / Phone Banking / Emails) has been removed in
 * favor of dedicated top-level sidebar entries:
 *   - Action Groups   -> /action-groups
 *   - Text Banking    -> /text-banking
 *   - Phone Banking   -> /phone-banking
 *   - Email           -> /email (still under the Voters group)
 *
 * The PeopleTable, EngagementPipeline, and FollowUpsList components
 * remain in `components/contacts/` for use elsewhere — they are simply
 * no longer composed into this page.
 */
function ContactsListPage() {
	useSetPageContext({
		page_key: "contacts.index",
		page_title: "Contacts",
		available_actions: ["create_task", "generate_content"],
		current_data_summary: null,
	});

	const session = useSession();
	const campaignId = session.campaign?.id;

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl space-y-6">
					<p className="text-muted-foreground">
						No campaign found. Please complete onboarding first.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30">
			{/* Page header */}
			<div className="bg-background border-b border-border px-4 sm:px-6 pt-6 pb-4">
				<div className="mx-auto max-w-7xl">
					<h1 className="text-2xl font-semibold">Contacts</h1>
					<p className="text-sm text-muted-foreground mt-0.5">
						Donors, volunteers, and other contacts on file for this campaign.
					</p>
				</div>
			</div>

			{/* Content */}
			<div className="mx-auto max-w-7xl px-6 py-6">
				<ContactsBrowser campaignId={campaignId} />
			</div>
		</div>
	);
}
